import { conflict, mergeKeyed, mergeObject, mergeRecord, pickValue, type MergeRegion, type MergeTally } from "./merge-rules.ts";
import { sameJson } from "./same-json.ts";
import { MAX_DOORS_PER_LAYER } from "./doors.ts";
import { COLLISION_CELLS_PER_TILE, type DoorDefinition, type DoorLayer, type EntityInstance, type LayerDocument, type PropInstance, type ZoneLayer } from "./types.ts";

/**
 * The layer half of the three-way merge (ADR-086 decision 6): a layer's own fields field by field, its grid
 * cell by cell, its props and entities item by item. Grid cells are compared as numbers that mean the same on
 * every side: terrain corners and collision bytes as they are, tile cells already packed against one tileset
 * list for all three sides (the caller's), and zone cells as the type id their layer's table names, numbered
 * here in one table for the three sides and written back canonical. A door layer's doors merge item by item, as
 * entities do, and its cells by the door id they name, numbered against the merged list; a cell naming a door the
 * merge removed holds none. A cell both sides changed differently falls in the region of its layer and chunk.
 */

/**
 * Where a layer lies and the grid geometry its cells map to tiles with.
 */
export interface LayerPlace {
  readonly tally: MergeTally;
  readonly mapId: string;
  /** The map's width and height in tiles. */
  readonly width: number;
  readonly height: number;
  /** Tiles per chunk side. */
  readonly chunkSize: number;
}

/**
 * The region of a whole layer.
 *
 * @param place The layer's map.
 * @param layerId The layer.
 * @returns The region.
 */
export function layerRegion(place: LayerPlace, layerId: string): MergeRegion {
  return { kind: "layer", id: JSON.stringify(["layer", place.mapId, layerId]), mapId: place.mapId, layerId };
}

/**
 * The region of one chunk of a layer's grid.
 *
 * @param place The layer's map.
 * @param layerId The layer.
 * @param chunkX The chunk's column.
 * @param chunkY The chunk's row.
 * @returns The region, its rectangle in tiles clipped to the map.
 */
function cellsRegion(place: LayerPlace, layerId: string, chunkX: number, chunkY: number): MergeRegion {
  const x = chunkX * place.chunkSize;
  const y = chunkY * place.chunkSize;
  return { kind: "cells", id: JSON.stringify(["cells", place.mapId, layerId, chunkX, chunkY]), mapId: place.mapId, layerId, rect: { x, y, w: Math.min(place.chunkSize, place.width - x), h: Math.min(place.chunkSize, place.height - y) } };
}

/**
 * How a grid's cells map to tiles: its width in cells and how many cells a tile spans along each axis. A
 * terrain layer's corner grid is one wider and taller than the map; its last row and column count with the
 * tiles before them.
 */
interface GridShape {
  readonly columns: number;
  readonly perTile: number;
}

/**
 * Merges a grid cell by cell.
 *
 * @param place The layer's map.
 * @param layerId The layer.
 * @param shape The grid's shape.
 * @param grids The base's, mine's and theirs' cells, all of one length.
 * @param out The array the merged cells are written to, of that length.
 * @returns `out`.
 */
function mergeCells<A extends Uint8Array | Uint16Array | Uint32Array>(place: LayerPlace, layerId: string, shape: GridShape, grids: { readonly base: ArrayLike<number>; readonly mine: ArrayLike<number>; readonly theirs: ArrayLike<number> }, out: A): A {
  const { base, mine, theirs } = grids;
  for (let i = 0; i < out.length; i += 1) {
    const m = mine[i] as number;
    const t = theirs[i] as number;
    if (m === t) {
      out[i] = m;
      continue;
    }
    const b = base[i] as number;
    if (m === b || t === b) {
      out[i] = m === b ? t : m;
      continue;
    }
    const tileX = Math.min(Math.floor((i % shape.columns) / shape.perTile), place.width - 1);
    const tileY = Math.min(Math.floor(Math.floor(i / shape.columns) / shape.perTile), place.height - 1);
    const side = conflict(place.tally, cellsRegion(place, layerId, Math.floor(tileX / place.chunkSize), Math.floor(tileY / place.chunkSize)));
    out[i] = side === "theirs" ? t : m;
  }
  return out;
}

/**
 * Merges one grid field of a layer, or takes it whole by the layer's region when the three grids differ in
 * length.
 *
 * @param place The layer's map.
 * @param layerId The layer.
 * @param shape The grid's shape.
 * @returns The field's merger.
 */
function gridMerger(place: LayerPlace, layerId: string, shape: GridShape): (base: unknown, mine: unknown, theirs: unknown) => unknown {
  return (base, mine, theirs) => {
    const b = base as Uint8Array | Uint32Array;
    const m = mine as Uint8Array | Uint32Array;
    const t = theirs as Uint8Array | Uint32Array;
    if (b.length !== m.length || m.length !== t.length || b.constructor !== m.constructor || m.constructor !== t.constructor) {
      return pickValue(place.tally, base, mine, theirs, () => layerRegion(place, layerId));
    }
    const out = m instanceof Uint32Array ? new Uint32Array(m.length) : new Uint8Array(m.length);
    return mergeCells(place, layerId, shape, { base: b, mine: m, theirs: t }, out);
  };
}

/**
 * Merges a zone layer's cells by the type ids they name and writes them canonical: its table the ids in the
 * order a row-major scan first meets them.
 *
 * @param place The layer's map.
 * @param layers The base's, mine's and theirs' layer.
 * @returns The merged `types` and `cells`.
 */
function mergeZoneCells(place: LayerPlace, layers: { readonly base: ZoneLayer; readonly mine: ZoneLayer; readonly theirs: ZoneLayer }): { readonly types: readonly string[]; readonly cells: Uint8Array } {
  const ids: string[] = [];
  const number = new Map<string, number>();
  const numbered = (layer: ZoneLayer): Uint16Array => {
    const table = layer.types.map((id) => {
      if (!number.has(id)) {
        ids.push(id);
        number.set(id, ids.length);
      }
      return number.get(id) as number;
    });
    return Uint16Array.from(layer.cells, (cell) => (cell === 0 ? 0 : (table[cell - 1] ?? 0)));
  };
  const merged = mergeCells(place, layers.mine.id, { columns: place.width * COLLISION_CELLS_PER_TILE, perTile: COLLISION_CELLS_PER_TILE }, { base: numbered(layers.base), mine: numbered(layers.mine), theirs: numbered(layers.theirs) }, new Uint16Array(layers.mine.cells.length));
  const types: string[] = [];
  const local = new Map<number, number>();
  const cells = new Uint8Array(merged.length);
  for (let i = 0; i < merged.length; i += 1) {
    const value = merged[i] as number;
    if (value === 0) {
      continue;
    }
    let index = local.get(value);
    if (index === undefined) {
      types.push(ids[value - 1] as string);
      index = types.length;
      local.set(value, index);
    }
    cells[i] = index;
  }
  return { types, cells };
}

/**
 * Merges a door layer's cells by the door ids they name and numbers them against the merged list of doors: a cell
 * whose door the merge removed holds none.
 *
 * @param place The layer's map.
 * @param layers The base's, mine's and theirs' layer.
 * @param doors The merged list of doors.
 * @returns The merged cells.
 */
function mergeDoorCells(place: LayerPlace, layers: { readonly base: DoorLayer; readonly mine: DoorLayer; readonly theirs: DoorLayer }, doors: readonly DoorDefinition[]): Uint8Array {
  const ids: string[] = [];
  const number = new Map<string, number>();
  const numbered = (layer: DoorLayer): Uint16Array => {
    const table = layer.doors.map((door) => {
      if (!number.has(door.id)) {
        ids.push(door.id);
        number.set(door.id, ids.length);
      }
      return number.get(door.id) as number;
    });
    return Uint16Array.from(layer.cells, (cell) => (cell === 0 ? 0 : (table[cell - 1] ?? 0)));
  };
  const merged = mergeCells(place, layers.mine.id, { columns: place.width * COLLISION_CELLS_PER_TILE, perTile: COLLISION_CELLS_PER_TILE }, { base: numbered(layers.base), mine: numbered(layers.mine), theirs: numbered(layers.theirs) }, new Uint16Array(layers.mine.cells.length));
  const placeOf = new Map(doors.map((door, i) => [door.id, i + 1]));
  return Uint8Array.from(merged, (value) => (value === 0 ? 0 : (placeOf.get(ids[value - 1] as string) ?? 0)));
}

/**
 * Merges a door layer: its doors item by item, each field by field and its `fields` key by key, then its cells by
 * the door they name. A merge that would list more than `MAX_DOORS_PER_LAYER` doors takes the layer whole by its
 * region.
 *
 * @param place The layer's map.
 * @param layers The base's, mine's and theirs' layer.
 * @param region The layer's region.
 * @returns The merged layer.
 */
function mergeDoorLayer(place: LayerPlace, layers: { readonly base: DoorLayer; readonly mine: DoorLayer; readonly theirs: DoorLayer }, region: () => MergeRegion): DoorLayer {
  const { base, mine, theirs } = layers;
  if (base.cells.length !== mine.cells.length || mine.cells.length !== theirs.cells.length) {
    return pickValue(place.tally, base, mine, theirs, region);
  }
  const item = (id: string): MergeRegion => itemRegion(place, mine.id, id);
  const doors = mergeKeyed<DoorDefinition>(place.tally, base.doors, mine.doors, theirs.doors, {
    region: item,
    content: (b, m, t) => mergeObject(place.tally, b, m, t, () => item(m.id), { fields: (bf, mf, tf) => mergeRecord(place.tally, bf, mf, tf, () => item(m.id)) ?? {} }),
    orderRegion: null,
  });
  if (doors.length > MAX_DOORS_PER_LAYER) {
    return pickValue(place.tally, base, mine, theirs, region);
  }
  const cells = mergeDoorCells(place, layers, doors);
  return mergeObject(place.tally, base, mine, theirs, region, { doors: () => doors, cells: () => cells });
}

/**
 * The region of a prop or an entity.
 *
 * @param place The layer's map.
 * @param layerId The layer.
 * @param itemId The item.
 * @returns The region.
 */
function itemRegion(place: LayerPlace, layerId: string, itemId: string): MergeRegion {
  return { kind: "item", id: JSON.stringify(["item", place.mapId, layerId, itemId]), mapId: place.mapId, layerId, itemId };
}

/**
 * A merged prop without a footprint of its own it may not hold (ADR-040 amendment of 2026-10-04). The merge
 * takes a prop field by field, so one side's cells can meet the other side's behaviour or the other side's
 * frame with no conflict; the cells are dropped when the merged prop carries a behaviour, which blocks by its
 * states, or shows another frame than the side that supplied the cells, since they are cells of that frame's
 * grid. This is what resolving the two edits as live operations leaves (`resolveUpdateProp`).
 *
 * @param merged The prop as merged field by field.
 * @param base The base's prop.
 * @param mine Mine's prop.
 * @param theirs Theirs' prop.
 * @returns The prop, without its footprint when it may not keep it.
 */
function withoutStrayFootprint(merged: PropInstance, base: PropInstance, mine: PropInstance, theirs: PropInstance): PropInstance {
  if (merged.footprint === undefined) {
    return merged;
  }
  const supplier = !sameJson(mine.footprint, base.footprint) && sameJson(mine.footprint, merged.footprint) ? mine : !sameJson(theirs.footprint, base.footprint) ? theirs : base;
  if (merged.behaviour === undefined && sameJson(merged.frame, supplier.frame)) {
    return merged;
  }
  const { footprint: _dropped, ...rest } = merged;
  return rest;
}

/**
 * Merges a layer's props: each prop field by field, its behaviour as one value, and its own footprint kept
 * only where it may stand (`withoutStrayFootprint`).
 *
 * @param place The layer's map.
 * @param layerId The layer.
 * @returns The `props` field's merger.
 */
function propsMerger(place: LayerPlace, layerId: string): (base: unknown, mine: unknown, theirs: unknown) => unknown {
  const region = (id: string): MergeRegion => itemRegion(place, layerId, id);
  return (base, mine, theirs) =>
    mergeKeyed<PropInstance>(place.tally, base as readonly PropInstance[], mine as readonly PropInstance[], theirs as readonly PropInstance[], {
      region,
      content: (b, m, t) => withoutStrayFootprint(mergeObject(place.tally, b, m, t, () => region(m.id)), b, m, t),
      orderRegion: null,
    });
}

/**
 * Merges a layer's entities: each entity field by field, its `fields` key by key.
 *
 * @param place The layer's map.
 * @param layerId The layer.
 * @returns The `entities` field's merger.
 */
function entitiesMerger(place: LayerPlace, layerId: string): (base: unknown, mine: unknown, theirs: unknown) => unknown {
  const region = (id: string): MergeRegion => itemRegion(place, layerId, id);
  return (base, mine, theirs) =>
    mergeKeyed<EntityInstance>(place.tally, base as readonly EntityInstance[], mine as readonly EntityInstance[], theirs as readonly EntityInstance[], {
      region,
      content: (b, m, t) => mergeObject(place.tally, b, m, t, () => region(m.id), { fields: (bf, mf, tf) => mergeRecord(place.tally, bf, mf, tf, () => region(m.id)) ?? {} }),
      orderRegion: null,
    });
}

/**
 * Merges one layer all three versions hold: its fields by the layer's region, its grid by chunk, its items by
 * item. A layer whose kind differs between the versions is taken whole by the layer's region.
 *
 * @param place The layer's map.
 * @param base The base's layer.
 * @param mine Mine.
 * @param theirs Theirs.
 * @returns The merged layer.
 */
export function mergeLayer(place: LayerPlace, base: LayerDocument, mine: LayerDocument, theirs: LayerDocument): LayerDocument {
  const region = (): MergeRegion => layerRegion(place, mine.id);
  if (base.kind !== mine.kind || mine.kind !== theirs.kind) {
    return pickValue(place.tally, base, mine, theirs, region);
  }
  const collision: GridShape = { columns: place.width * COLLISION_CELLS_PER_TILE, perTile: COLLISION_CELLS_PER_TILE };
  switch (mine.kind) {
    case "terrain":
      return mergeObject(place.tally, base, mine, theirs, region, { corners: gridMerger(place, mine.id, { columns: place.width + 1, perTile: 1 }) });
    case "tile":
      return mergeObject(place.tally, base, mine, theirs, region, { tiles: gridMerger(place, mine.id, { columns: place.width, perTile: 1 }) });
    case "collision":
      return mergeObject(place.tally, base, mine, theirs, region, { cells: gridMerger(place, mine.id, collision) });
    case "zone": {
      const layers = { base: base as ZoneLayer, mine, theirs: theirs as ZoneLayer };
      if (layers.base.cells.length !== mine.cells.length || mine.cells.length !== layers.theirs.cells.length) {
        return pickValue(place.tally, base, mine, theirs, region);
      }
      const zones = mergeZoneCells(place, layers);
      return mergeObject(place.tally, base, mine, theirs, region, { types: () => zones.types, cells: () => zones.cells });
    }
    case "prop":
      return mergeObject(place.tally, base, mine, theirs, region, { props: propsMerger(place, mine.id) });
    case "entity":
      return mergeObject(place.tally, base, mine, theirs, region, { entities: entitiesMerger(place, mine.id) });
    case "door":
      return mergeDoorLayer(place, { base: base as DoorLayer, mine, theirs: theirs as DoorLayer }, region);
  }
}
